import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Heading, Spinner, Text, VStack, HStack } from '@chakra-ui/react';
import { ConsentService } from '@/services/ChatConsentService';
import useChatTheme from '../chat/hooks/useChatTheme';

const DefaultPolicy = ({ data, colors }) => {
    const { t } = useTranslation('chat');
    const vars = {
        company: data?.companyName || t('consent.companyFallback'),
        address: data?.companyAddress ? `, ${data.companyAddress}` : '',
        nip: data?.companyNip ? ` (NIP ${data.companyNip})` : '',
    };
    const sections = t('privacy.default.sections', { ...vars, returnObjects: true });

    return (
        <VStack align="stretch" gap={6}>
            {Array.isArray(sections) && sections.map((s) => (
                <Box key={s.title}>
                    <Heading as="h2" fontSize="md" fontWeight="700" mb={2}>{s.title}</Heading>
                    <Text fontSize="sm" lineHeight="1.7" whiteSpace="pre-wrap" style={{ color: colors.textSecondary }}>
                        {s.body}
                    </Text>
                </Box>
            ))}
        </VStack>
    );
};

const PrivacyPolicyPage = () => {
    const { t } = useTranslation('chat');
    const { recruitmentId } = useParams();
    const colors = useChatTheme(false);
    const [state, setState] = useState({ status: 'loading', data: null });

    useEffect(() => {
        let cancelled = false;
        ConsentService.getPrivacyPolicy(recruitmentId)
            .then(data => { if (!cancelled) setState({ status: 'ready', data }); })
            .catch(() => { if (!cancelled) setState({ status: 'error', data: null }); });
        return () => { cancelled = true; };
    }, [recruitmentId]);

    useEffect(() => {
        document.title = t('privacy.title');
    }, [t]);

    return (
        <Box minH="100dvh" px={4} py={{ base: 8, md: 14 }} style={{ background: colors.pageBg, color: colors.textPrimary }}>
            <Box
                maxW="760px"
                mx="auto"
                p={{ base: 5, md: 10 }}
                borderRadius="20px"
                borderWidth="1px"
                style={{ background: colors.chatBg, borderColor: colors.borderColor }}
            >
                <Heading as="h1" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" mb={2}>
                    {t('privacy.title')}
                </Heading>
                {state.data?.companyName && (
                    <Text fontSize="sm" mb={8} style={{ color: colors.textSecondary }}>{state.data.companyName}</Text>
                )}

                {state.status === 'loading' && (
                    <HStack justify="center" py={10}><Spinner color={colors.avatarBg} /></HStack>
                )}
                {state.status === 'error' && (
                    <Text fontSize="sm" style={{ color: colors.textSecondary }}>{t('privacy.loadError')}</Text>
                )}
                {state.status === 'ready' && (
                    state.data?.content ? (
                        <Text fontSize="sm" lineHeight="1.7" whiteSpace="pre-wrap">{state.data.content}</Text>
                    ) : (
                        <DefaultPolicy data={state.data} colors={colors} />
                    )
                )}
            </Box>
        </Box>
    );
};

export default PrivacyPolicyPage;
